import type Phaser from "phaser";
import { Capacitor } from "@capacitor/core";
import type { SaveAdapter } from "@yjsgames/save";
import { loadThemeFont } from "@yjsgames/theme";
import { createGame } from "./game/createGame.js";
import { createDomSafeAreaSource } from "./game/safeAreaSource.js";
import { resolveDesignHeight } from "./game/responsiveDesign.js";
import { BrickSettingsStore } from "./systems/BrickSettingsStore.js";
import { resolveTargetFps } from "@yjsgames/perf";
import { readFirebaseEnv } from "./firebase/firebaseEnv.js";
import { deleteAnonymousUser, initFirebase, type FirebaseHandle } from "./firebase/firebaseClient.js";
import { createAccountLinkProvider } from "./account/createAccountLinkProvider.js";
import { createSupportShareProvider } from "./support/createSupportShareProvider.js";
import type { SupportShareProvider } from "@yjsgames/support-contact";
import { createCrashlytics } from "./crashlytics/createCrashlytics.js";
import { installCrashlyticsErrorBridge } from "./crashlytics/crashlyticsErrorBridge.js";
import type { CrashlyticsPort } from "@yjsgames/crashlytics-ports";
import { createLogCollector } from "@yjsgames/log-collector";
import type { AccountLinkProvider } from "@yjsgames/account-link";
import { fetchFeatureOverrides } from "./firebase/featureRemoteConfig.js";
import { APP_FLAVOR, APP_VERSION, CHEAT_ENABLED, FORCE_TARGET_FPS } from "./env/appFlavor.js";
import { resolveFeatureFlags, type FeatureFlags } from "./env/featureFlags.js";
import { createSaveAdapter, createLocalSaveAdapter } from "./save/createSaveAdapter.js";
import {
  EnvTaggingSink,
  NoopAnalyticsSink,
  PlatformTaggingSink,
  type AnalyticsSink,
} from "@yjsgames/analytics-ports";
import { FirebaseAnalyticsSink } from "./analytics/FirebaseAnalyticsSink.js";
import { AnalyticsEvent } from "./analytics/analyticsEvents.js";
import { createGpgs } from "./gpgs/createGpgs.js";
import { GpgsAchievementSink } from "./gpgs/GpgsAchievementSink.js";
import { installGpgsDebugGlobal } from "./gpgs/installGpgsDebugGlobal.js";
import type { GpgsPort } from "@yjsgames/gpgs-ports";
import { createLeaderboardClient } from "./leaderboard/createLeaderboardClient.js";
import { ChapterRankingLeaderboard } from "./leaderboard/ChapterRankingLeaderboard.js";
import { ChapterLeaderboardSink } from "./leaderboard/ChapterLeaderboardSink.js";
import { THEME } from "./themes/activeTheme.js";
import { PALETTE } from "./themes/palette.js";
import { installLocalizedAppTitle } from "./i18n/installAppTitle.js";
import { installScreenWakeLock } from "./wakeLock/installScreenWakeLock.js";
import { installGlobalErrorTrap, showFatalErrorOverlay } from "./diag/globalErrorTrap.js";
import {
  currentCrazyGamesSdk,
  initCrazyGamesSdk,
  isCrazyGamesWebBuild,
} from "./ads/CrazyGamesSdk.js";

export { DESIGN_WIDTH, DESIGN_HEIGHT } from "./game/createGame.js";

let game: Phaser.Game | null = null;

const DEVICE_PREVIEW_BUILD_ENABLED = import.meta.env.DEV;

/** Phaser.Game 인스턴스 접근자 — Phase 4 AutoPlay Bootstrap의 Scene lookup에 사용. */
export function getGame(): Phaser.Game | null {
  return game;
}

async function bootGame(
  saveAdapter: SaveAdapter,
  settingsAdapter: SaveAdapter,
  analyticsSink: AnalyticsSink,
  featureFlags: FeatureFlags,
  onDeleteCloudAccount?: () => void,
  accountLink?: AccountLinkProvider,
  supportShare?: SupportShareProvider,
  collectSupportLog?: () => string,
  crashlytics?: CrashlyticsPort,
): Promise<void> {
  // 목표 FPS를 game 생성 전에 1회 선로드 — config.fps는 생성 시점에 필요(120Hz 패널 발열 캡을 첫 프레임부터).
  // SettingsFlow도 부팅 중 load하지만 그건 상태 동기화용. displayHz=240: 후보 상한 무제한(auto=60 시작, 고정값 그대로).
  const settings = await new BrickSettingsStore(settingsAdapter).load();
  const initialTargetFps = FORCE_TARGET_FPS ?? resolveTargetFps(settings?.targetFps ?? "auto", 240);
  // config 생성 + registry 주입은 createGame(테스트 공유 헬퍼)으로 추출 — 스모크 테스트가 동일 경로를 부팅한다.
  // safeAreaSource(DOM 어댑터)는 웹·네이티브 공통 — GameScene.create가 read()로 디바이스 inset을 읽어 레이아웃 압축.
  game = createGame({
    saveAdapter,
    settingsAdapter,
    analyticsSink,
    featureFlags,
    safeAreaSource: createDomSafeAreaSource(),
    initialTargetFps,
    // 화면 비율 추종 동적 design 높이 — 실기기(네이티브)는 #game-root이 곧 화면이라 이 1회로 정확.
    // 웹 시뮬레이터는 mountDeviceFrame을 createGame 전에 호출해 #game-root이 이미 device px이므로 정확.
    designHeight: readInitialDesignHeight(),
    onDeleteCloudAccount,
    accountLink,
    supportShare,
    collectSupportLog,
    crashlytics,
  });
}

/** 부팅 시점 캔버스 비율 — #game-root 레이아웃 px(없으면 window) 기준 동적 design 높이. */
function readInitialDesignHeight(): number {
  const root = document.getElementById("game-root");
  const width = root && root.clientWidth > 0 ? root.clientWidth : window.innerWidth;
  const height = root && root.clientHeight > 0 ? root.clientHeight : window.innerHeight;
  return resolveDesignHeight({ width, height });
}

/**
 * 테마 배경색을 DOM에 반영 — index.html의 정적 #0d0d14는 부팅 전 placeholder이고,
 * 여기서 테마 팔레트로 덮는다 (safe-area padding 영역 + meta theme-color 포함).
 */
function applyThemeBackground(): void {
  document.body.style.background = PALETTE.appBackground;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", PALETTE.appBackground);
}

/**
 * 외부 서비스 결정(주입) — "로컬이냐 Firestore냐" + Analytics 활성 여부를 여기서 정한다.
 * Firebase env가 설정돼 있으면 익명 인증으로 UID를 받아 write-behind 클라우드 세이브 + GA4를, 아니면 로컬 단독 + Noop을 구성.
 * 익명 인증이 오래 걸리거나 오프라인이면 5s 후 로컬 단독으로 부팅해 게임 시작 지연을 막는다.
 */
async function resolveServices(): Promise<{
  saveAdapter: SaveAdapter;
  settingsAdapter: SaveAdapter;
  analyticsSink: AnalyticsSink;
  featureFlags: FeatureFlags;
  /** 계정 삭제 시 익명 Firebase 유저 제거 콜백 — 클라우드 결선 시에만(미결선이면 undefined). */
  onDeleteCloudAccount?: () => void;
  /** 계정 연동(익명→Play Games/Game Center) Provider — 클라우드+네이티브일 때만(웹/로컬단독은 undefined → 버튼 숨김). */
  accountLink?: AccountLinkProvider;
  /** 익명/연동 계정 uid — 문의 메일 로그 헤더 추적용(클라우드 결선 시에만, 미결선이면 undefined). */
  userId?: string;
  /** Crashlytics Port(네이티브 크래시 + JS 에러 비치명 기록). 웹은 Noop. 항상 반환(분기 불필요). */
  crashlytics: CrashlyticsPort;
}> {
  const isNative = Capacitor.isNativePlatform();
  const env = readFirebaseEnv();
  let handle: FirebaseHandle | null = null;
  if (env) {
    handle = await Promise.race([
      initFirebase(env),
      new Promise<FirebaseHandle | null>((resolve) => setTimeout(() => resolve(null), 5000)),
    ]);
  }
  const cloud = handle?.uid ? { db: handle.db, uid: handle.uid } : null;
  // Crashlytics — 네이티브 크래시 수집 + (boot의) JS 에러 비치명 기록. 웹은 Noop. 진단 컨텍스트(버전/플랫폼/
  // flavor)를 커스텀 키로, 크래시 귀속용 userId를 익명 UID로 결선. 네이티브는 어댑터 로드까지 버퍼링.
  const crashlytics = createCrashlytics();
  crashlytics.setCustomKey("app_version", APP_VERSION);
  crashlytics.setCustomKey("platform", Capacitor.getPlatform());
  crashlytics.setCustomKey("flavor", APP_FLAVOR);
  if (cloud) crashlytics.setUserId(cloud.uid);
  const saveAdapter = createSaveAdapter({ isNative, cloud });
  // 설정(사운드 볼륨/언어/표기형식)은 로컬 전용 — 클라우드 동기화에서 제외(기기별 설정).
  const settingsAdapter = createLocalSaveAdapter(isNative);
  const baseSink: AnalyticsSink = handle?.analytics
    ? new FirebaseAnalyticsSink(handle.analytics)
    : new NoopAnalyticsSink();
  // 2축 태깅 — platform(web/native) + environment(dev/live). dev 트래픽을 live 출시 데이터와 분리 집계해
  // GA4/ga-export에서 dev 오염 필터링(출시 전 dev 프로젝트 핵심). 합성: base ← platform ← env.
  const platformSink = new PlatformTaggingSink(baseSink, isNative ? "native" : "web");
  const taggedSink: AnalyticsSink = new EnvTaggingSink(platformSink, APP_FLAVOR);
  // GPGS 업적/리더보드 sink — 이미 발화하는 게임 이벤트(chapter_clear/grid_unlock/...)를 업적 해금·점수
  // 제출로 변환(게임 로직 결합 0). 웹/비-Android는 Noop port라 무해. port는 dynamic import로 네이티브
  // 어댑터 교체 후 갱신된다. 부팅 중 자동 signIn은 하지 않는다. GPGS가 미설정인 디바이스/에뮬레이터에서도
  // 로그인 UI를 띄우지 않고 게스트(익명 Firebase 또는 로컬 저장) 흐름으로 그대로 진행하기 위함이다.
  let gpgsPort: GpgsPort = createGpgs({
    onNativeReady: (port) => {
      gpgsPort = port;
    },
  });
  const achievementSink: AnalyticsSink = new GpgsAchievementSink(taggedSink, () => gpgsPort);
  // 리더보드(플랫폼 무관) — Android=GPGS / iOS=Game Center 클라이언트 + 챕터 랭킹 제출기(추상 상속).
  // chapter_clear 이벤트의 chapter 점수를 백엔드에 제출(최고 챕터 랭킹). 웹은 Noop 클라이언트라 무해.
  const leaderboardClient = createLeaderboardClient(() => gpgsPort);
  const chapterLeaderboard = new ChapterRankingLeaderboard(leaderboardClient);
  const analyticsSink: AnalyticsSink = new ChapterLeaderboardSink(achievementSink, chapterLeaderboard);
  // dev/치트 빌드 — 콘솔/AutoPlay용 GPGS 디버그 글로벌(window.__gpgs). 라이브는 미설치(번들 0).
  installGpgsDebugGlobal(() => gpgsPort);
  // 빌드별 기능 플래그 — 정적 기본(웹 live만 off) 위에 RemoteConfig 킬스위치(OFF-only)를 얹는다.
  // 전 기능 off(웹 live)면 OFF-only 특성상 켤 수 없으므로 RC fetch 자체를 스킵(RC 모듈 미로드).
  const webReleaseChannel = isCrazyGamesWebBuild() ? "crazygames" : "default";
  const base = resolveFeatureFlags(isNative, APP_FLAVOR, {}, undefined, undefined, webReleaseChannel);
  let featureFlags = base;
  if (handle && (base.dailyShop || base.dungeon || base.houseAds)) {
    const overrides = await fetchFeatureOverrides(handle.app, base);
    featureFlags = resolveFeatureFlags(
      isNative,
      APP_FLAVOR,
      overrides,
      undefined,
      undefined,
      webReleaseChannel,
    );
  }
  // 클라우드 결선(익명 UID 확보) 시에만 — 계정 삭제 시 익명 유저 제거(orphan 방지). app 캡처.
  // Firebase app만 있고 인증은 실패한 로컬 단독 fallback에서는 웹 계정 삭제가 Auth 네트워크 에러를 남기면 안 된다.
  const app = handle?.app;
  const onDeleteCloudAccount = cloud && app ? (): void => void deleteAnonymousUser(app) : undefined;
  // 계정 연동 Provider — 클라우드(익명 인증 성공) + 네이티브(Android/iOS)일 때만. 웹은 팩토리가 null
  // 반환(GPGS/Game Center 부재) → undefined로 정규화 → 설정 모달이 버튼 숨김. uid 보존 link.
  const accountLink: AccountLinkProvider | undefined =
    (cloud && handle ? createAccountLinkProvider({ auth: handle.auth }) : null) ?? undefined;
  return {
    saveAdapter,
    settingsAdapter,
    analyticsSink,
    featureFlags,
    onDeleteCloudAccount,
    accountLink,
    userId: cloud?.uid,
    crashlytics,
  };
}

/**
 * 디바이스 프레임 마운트 — 웹 dev에서만. **createGame 전에 호출**해 #game-root을 디바이스 px로
 * 확정시킨다. live 웹 빌드는 실제 배포 표면이므로 시뮬레이터/베젤 없이 전체 화면을 사용한다.
 */
async function mountDeviceFrame(): Promise<void> {
  if (Capacitor.isNativePlatform()) return;
  const { mountDevicePreview, DevicePresets } = await import("@yjsgames/device-preview");
  const withoutHomeIndicator = <T extends { homeIndicator?: boolean }>(preset: T): T => ({
    ...preset,
    homeIndicator: false,
  });
  mountDevicePreview({
    target: "#game-root",
    presets: [
      withoutHomeIndicator(DevicePresets.iPhone15),
      withoutHomeIndicator(DevicePresets.iPhone14Pro),
      withoutHomeIndicator(DevicePresets.galaxyS24),
      withoutHomeIndicator(DevicePresets.zFlip6Unfolded),
      withoutHomeIndicator(DevicePresets.iPadMini),
    ],
    defaultDeviceId: "iPhone15",
    persistKey: "module-brick-idle",
    allowCustom: true,
  });
}

/**
 * 디바이스 시뮬레이터 scale 보정 — **createGame 후 호출**. `.dvp-frame`은 창이 작으면 transform:
 * scale(s<1)로 축소되는데, Phaser의 parent 크기 재계산(getParentBounds)은 getBoundingClientRect,
 * 즉 transform이 곱해진 "시각 크기"를 parentSize로 덮어써 s² 이중 축소가 된다. → getParentBounds를
 * 레이아웃 px(clientWidth/Height, transform 면역) 기반으로 교체한다. 디바이스 전환으로 design 높이가
 * 바뀌면 GameScene이 `devicepreviewchange`에서 design height를 갱신하고 씬만 재시작한다.
 */
async function patchSimulatorScale(): Promise<void> {
  if (Capacitor.isNativePlatform()) return;
  const root = document.getElementById("game-root");
  const game = getGame();
  if (!root || !game) {
    // 패치 누락 시 s² 이중 축소가 재발하므로 silent skip 금지 — 부팅 순서 회귀를 즉시 드러낸다.
    console.warn("[simulatorScaleSync] game/root 미준비 — scale 패치 스킵");
    return;
  }
  const { createLayoutParentBounds } = await import("./game/simulatorScaleSync.js");
  const sync = createLayoutParentBounds(
    () => ({ width: root.clientWidth, height: root.clientHeight }),
    game.scale,
  );
  game.scale.getParentBounds = sync;
  // ⚠ bare refresh()는 stale parentSize로 fit을 먼저 적용한 뒤 말미에야 parentSize를 갱신해 구형
  // fit이 고착된다 → 반드시 "크기 갱신(sync) → 변경 시 refresh" 순서.
  const apply = (): void => {
    if (sync()) game.scale.refresh();
  };
  apply(); // mount 직후 1회 — 디바이스 frame px 반영
  new ResizeObserver(apply).observe(root); // 디바이스 frame transform 변동(창 리사이즈) 대응
}

async function boot(): Promise<void> {
  // 디바이스 언어로 브라우저 탭 제목(document.title) + <html lang> 현지화 — 첫 화면부터 노출되게
  // 부팅 최우선 1회. 인게임 설정 언어가 아닌 navigator.language 기준(OS 라벨 성격).
  installLocalizedAppTitle();
  // 전역 JS 에러 트랩 — 부팅 극초기부터 설치해 uncaught error/rejection을 (1) console.error로 재발행해
  // logCollector·consoleCapture에 누적 + (2) 화면 오버레이로 노출한다. 실기기/TestFlight처럼 콘솔을
  // 못 보는 환경에서 흰 화면 대신 원인을 드러내는 계측(crashlyticsErrorBridge는 네이티브 수집 — plist
  // 미설정 iOS에선 Noop이라 무표시 → 오버레이가 그 공백을 메운다). ⚠ 네이티브 NSException(AdMob/Firebase
  // plist 누락)은 JS로 못 잡음 — Xcode Device Logs / Crashlytics 필요(plan Track 1·3).
  installGlobalErrorTrap({
    window,
    onError: ({ message }) => {
      console.error(message);
      showFatalErrorOverlay(message);
    },
  });
  // 방치형 게임 특성상 전투를 오래 켜 두는 동안 화면 자동 잠금을 막는다.
  // Web은 Screen Wake Lock API, Capacitor native는 로컬 ScreenWakeLock 플러그인으로 결선된다.
  installScreenWakeLock();
  // 콘솔 error/warn + uncaught를 파일로 캡처 — 에이전트가 VSCode 디버그 콘솔/실기기 logcat을 직접
  // 못 읽으므로 런타임 에러를 파일로 노출하는 DIAG 경로. facade 활성화 여부와 무관하게 설치한다.
  //  - 웹 dev: vite plugin `/__autoplay/session/console` (prod 번들 0KB — DEV 가드 dynamic import)
  //  - 네이티브 dev flavor(APK): Capacitor Filesystem `autoplay/<project>/console.log` (PC는 adb pull)
  if (import.meta.env.DEV) {
    const { installConsoleCapture } = await import("@yjsgames/autoplay/browser");
    installConsoleCapture();
  } else if (Capacitor.isNativePlatform() && CHEAT_ENABLED) {
    const { installCapacitorConsoleCapture } = await import("@yjsgames/autoplay/capacitor");
    installCapacitorConsoleCapture({ projectName: "module-brick-idle" });
  }
  // 문의하기(고객 지원) 로그 수집기 — 부팅 즉시 console/window 에러를 메모리 ring buffer에 누적해
  // 설정 모달 문의 시 첨부한다. 라이브 포함 항상 설치(문의는 라이브에서도 동작). consoleCapture(전송)와
  // 별개 공존 — 둘 다 console을 래핑하지만 각자 원본을 호출하므로 안전.
  const logCollector = createLogCollector();
  logCollector.install();
  const crazyGamesSdk = await initCrazyGamesSdk(isCrazyGamesWebBuild());
  crazyGamesSdk?.game.loadingStart?.();
  applyThemeBackground();
  // 테마 폰트 명시 preload — Phaser canvas Text는 폰트 로드를 자동 대기하지 않으므로
  // Game 생성 전에 끝내 첫 프레임부터 테마 폰트로 렌더한다. 실패해도 fallback(sans-serif)으로 진행.
  await loadThemeFont(THEME.font);
  const {
    saveAdapter,
    settingsAdapter,
    analyticsSink,
    featureFlags,
    onDeleteCloudAccount,
    accountLink,
    userId,
    crashlytics,
  } = await resolveServices();
  // 전역 JS 에러(uncaught + unhandledrejection) → Crashlytics 비치명 기록. Phaser 게임은 네이티브 크래시보다
  // JS 런타임 에러가 실질 신호. logCollector(문의 메일)·consoleCapture(dev 파일)와 독립 공존. 웹은 Noop이라 무해.
  installCrashlyticsErrorBridge(crashlytics);
  // 문의 공유(native share / 웹 mailto) + 첨부 로그 조립. deviceMeta는 호출 시점에 평가(언어/플랫폼).
  const supportShare = createSupportShareProvider();
  const collectSupportLog = (): string =>
    logCollector.getLog({
      platform: Capacitor.getPlatform(),
      appVersion: APP_VERSION,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      language: typeof navigator !== "undefined" ? navigator.language : "",
      userId,
    });
  analyticsSink.log(AnalyticsEvent.AppLaunch);
  // ⚠ dev 웹에서만 디바이스 프레임을 createGame 전에 mount — live 웹 빌드는 배포 표면 그대로 전체 화면.
  if (DEVICE_PREVIEW_BUILD_ENABLED) await mountDeviceFrame();
  await bootGame(
    saveAdapter,
    settingsAdapter,
    analyticsSink,
    featureFlags,
    onDeleteCloudAccount,
    accountLink,
    supportShare,
    collectSupportLog,
    crashlytics,
  );
  if (DEVICE_PREVIEW_BUILD_ENABLED) await patchSimulatorScale();
  currentCrazyGamesSdk()?.game.loadingStop?.();
  currentCrazyGamesSdk()?.game.gameplayStart();
  analyticsSink.log(AnalyticsEvent.GameReady);
}
void boot();

/**
 * AutoPlay QA facade — 플랫폼별 가드로만 활성화 (그 외 prod 번들은 dynamic import라 로드 0):
 *  - 웹 dev: `import.meta.env.DEV` + `VITE_ENABLE_AUTOPLAY=true` (Vite plugin 파일 RPC).
 *    `AUTOPLAY_SCREENSHOT=true`로 dev 서버를 띄우면 playwright screenshot driver도 활성화.
 *  - 네이티브 dev flavor(`apk:dev`): `CHEAT_ENABLED` + `VITE_ENABLE_AUTOPLAY=true`
 *    (Capacitor 파일 RPC — PC가 adb push/pull). 기본 dev 플레이는 폴링 비용 방지를 위해 비활성.
 *    live APK는 CHEAT_ENABLED=false라 비활성.
 */
async function setupAutoPlay(): Promise<void> {
  const enabledByEnv = import.meta.env["VITE_ENABLE_AUTOPLAY"] === "true";
  const webDev = import.meta.env.DEV && enabledByEnv;
  const nativeDev = Capacitor.isNativePlatform() && CHEAT_ENABLED && enabledByEnv;
  if (!webDev && !nativeDev) return;
  const { setupAutoPlay: boot } = await import("./autoplay/AutoPlayBootstrap.js");
  await boot({ getGame });
}
void setupAutoPlay();
